import React from 'react';
import { motion } from 'framer-motion';
import { Palette, ShoppingBag, Sparkles, ArrowUpRight } from 'lucide-react';

const easeLuxury = [0.22, 1, 0.36, 1];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: easeLuxury } },
};

const SERVICES = [
  {
    title: 'Oto Boyama',
    description: 'Fabrika çıkışı kalitesinde, fırın boyama teknolojisiyle aracınıza yeniden hayat veriyoruz.',
    icon: Palette,
    gradient: 'from-red-950 via-bg-secondary to-bg-primary',
    span: 'lg:col-span-2 lg:row-span-2',
    minH: 'min-h-[420px] lg:min-h-[520px]',
  },
  {
    title: 'Boya Ürünleri Satışı',
    description: 'DYO, BASF ve Sintaş gibi premium markaların geniş ürün yelpazesi elimizin altında.',
    icon: ShoppingBag,
    gradient: 'from-amber-950 via-bg-secondary to-bg-primary',
    span: 'lg:col-span-1',
    minH: 'min-h-[260px]',
  },
  {
    title: 'Renk Danışmanlığı',
    description: 'Aracınıza en uygun rengi bulmanız için profesyonel danışmanlık ve özel renk eşleştirme hizmeti sunuyoruz.',
    icon: Sparkles,
    gradient: 'from-red-950 via-bg-secondary to-bg-primary',
    span: 'lg:col-span-1',
    minH: 'min-h-[260px]',
  },
];

export default function Services() {
  return (
    <section id="services" className="bg-bg-primary py-20 sm:py-28 lg:py-36">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.7, ease: easeLuxury }}
          className="mb-10 sm:mb-16 space-y-3 sm:space-y-4"
        >
          <p className="text-accent-gold font-semibold tracking-[0.3em] uppercase text-sm">Hizmetlerimiz</p>
          <h2 className="text-3xl sm:text-4xl md:text-6xl font-black text-white tracking-tightest">Ne Yapıyoruz</h2>
        </motion.div>

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-100px' }}
          className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5"
        >
          {SERVICES.map((service) => (
            <ServiceCard key={service.title} service={service} />
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function ServiceCard({ service }) {
  const Icon = service.icon;
  return (
    <motion.div
      variants={fadeUp}
      className={`group relative ${service.span} ${service.minH} rounded-2xl overflow-hidden border border-white/10 hover:border-accent-gold transition-colors duration-700`}
    >
      {/* Background */}
      <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} scale-100 group-hover:scale-110 transition-transform duration-700 ease-luxury`} />
      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent group-hover:from-black/98 transition-all duration-700" />

      {/* Content */}
      <div className="relative h-full flex flex-col justify-end p-6 sm:p-8 lg:p-10">
        <div className="transform lg:group-hover:-translate-y-2 transition-transform duration-700 ease-luxury">
          <Icon className="text-accent-gold mb-4 sm:mb-5" size={32} strokeWidth={1.5} />
          <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white tracking-tight flex items-center gap-2">
            {service.title}
            <ArrowUpRight className="opacity-0 lg:group-hover:opacity-100 -translate-x-2 lg:group-hover:translate-x-0 transition-all duration-500 text-accent-gold hidden lg:inline" size={22} />
          </h3>

          <div className="grid grid-rows-[1fr] lg:grid-rows-[0fr] lg:group-hover:grid-rows-[1fr] transition-all duration-700 ease-luxury">
            <div className="overflow-hidden">
              <p className="text-text-muted font-light pt-3 max-w-md leading-relaxed text-sm sm:text-base">
                {service.description}
              </p>
              <div className="mt-4 sm:mt-5 h-px w-16 bg-gradient-to-r from-accent-gold to-transparent" />
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
